// Updated SimilarArtist.tsx - Professional UI improvements
import React, { useState } from 'react';
import { useFormikContext, ErrorMessage } from 'formik';
import { RequiredAsterisk } from '../../../components/common/RequiredAsterisk';
import { PitchFormValues } from '../Schema/pitchValidationSchema';
import InfoTooltip from "../../../components/common/InfoTooltip";


export const SimilarArtist = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<PitchFormValues>();
  const [inputValue, setInputValue] = useState('');

  const currentArtists: string[] = values.similarArtists
    ? values.similarArtists.split('||').map(a => a.trim()).filter(a => a)
    : [];

  const isMaxReached = currentArtists.length >= 3;
  const hasError = errors.similarArtists && touched.similarArtists;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addArtist();
    }
  };

  const addArtist = () => {
    const newArtist = inputValue.trim();
    if (
      newArtist &&
      !currentArtists.includes(newArtist) &&
      currentArtists.length < 3
    ) {
      const updated = [...currentArtists, newArtist];
      setFieldValue('similarArtists', updated.join(' || '));
      setInputValue('');
    }
  };

  const handleRemoveArtist = (index: number) => {
    const updated = [...currentArtists];
    updated.splice(index, 1);
    setFieldValue('similarArtists', updated.join(' || '));
  };

  return (
    <div className="mt-4">
      <label htmlFor="similar-artists" className="flex items-center gap-1 mb-2 text-sm font-medium text-neutral-700">
        Similar Artists <RequiredAsterisk />
        <InfoTooltip content="Amazon <br> Select up to three artists with comparable sound for algorithmic recommendations and related
playlist placement.
" />
      </label>
      <div className={`w-full border rounded-md bg-white transition-colors 
      ${hasError ? 'border-danger-500' : 'border-neutral-300 focus-within:border-primary-500'
        }`}>
        {currentArtists.length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 border-b border-neutral-100">
            {currentArtists.map((artist, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-800 bg-primary-100 rounded-full gap-2"
              >
                <span>{artist}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveArtist(index)}
                  className="flex items-center justify-center w-4 h-4 text-primary-700 transition-colors rounded-full hover:bg-primary-200 hover:text-primary-800"
                  title={`Remove ${artist}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="relative flex items-center">
          <input
            type="text"
            id="similar-artists"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isMaxReached
                ? "Maximum 3 artists selected"
                : "Type artist name and press Enter"
            }
            disabled={isMaxReached}
            className="flex-1 px-3 py-2 text-sm text-neutral-700 placeholder-neutral-500 bg-transparent border-none outline-none disabled:text-neutral-400 disabled:cursor-not-allowed"
          />
          {!isMaxReached && inputValue.trim() && (
            <button
              type="button"
              onClick={addArtist}
              className="flex items-center justify-center w-8 h-8 mr-2 text-primary-700 transition-colors rounded-full hover:bg-primary-50"
              title="Add artist"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-neutral-500">
          {currentArtists.length}/3 artists selected
        </span>
        {!isMaxReached && (
          <span className="text-xs text-neutral-400">
            Press Enter or click + to add
          </span>
        )}
      </div>
      <ErrorMessage name="similarArtists" component="div" className="mt-1 text-sm text-danger-600" />
    </div>
  );
};
